import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Sync as SyncIcon, LinkOff as LinkOffIcon, Link as LinkIcon } from '@mui/icons-material';
import { StravaProfile } from '../../types/user.types';
import { STRAVA_ORANGE } from '../../theme/appColorTokens';
import { SurfaceCard } from '../ui/SurfaceCard';
import { StatusPill } from '../ui/StatusPill';

interface ProfileStravaCardProps {
  stravaProfile?: StravaProfile;
  syncing: boolean;
  onSync: () => void;
  onDisconnect: () => void;
  onConnect: () => void;
}

// Strava connection status with sync / disconnect (or connect) actions.
export const ProfileStravaCard = ({ stravaProfile, syncing, onSync, onDisconnect, onConnect }: ProfileStravaCardProps) => {
  const { t } = useTranslation('profile');
  const fullName = stravaProfile ? [stravaProfile.firstname, stravaProfile.lastname].filter(Boolean).join(' ') : '';

  return (
    <SurfaceCard
      title="Strava"
      action={<StatusPill tone={stravaProfile ? 'success' : 'warn'} label={stravaProfile ? t('strava.connected') : t('strava.notConnected')} />}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        <Box sx={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
          {stravaProfile
            ? [fullName && t('strava.signedInAs', { name: fullName }), t('strava.autoSync')].filter(Boolean).join(' ')
            : t('strava.connectPrompt')}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {stravaProfile ? (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
                onClick={onSync}
                disabled={syncing}
              >
                {syncing ? t('strava.syncing') : t('strava.syncData')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<LinkOffIcon />}
                onClick={onDisconnect}
                sx={{ color: 'var(--danger)' }}
              >
                {t('strava.disconnect')}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<LinkIcon />}
              onClick={onConnect}
              sx={{ background: STRAVA_ORANGE, color: '#fff', '&:hover': { background: '#e34402' } }}
            >
              {t('strava.connect')}
            </Button>
          )}
        </Box>
      </Box>
    </SurfaceCard>
  );
};
